import { IMsalContext, useMsal } from "@azure/msal-react";
import { 
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
  useTheme,
  Autocomplete,
  TextField,
  Chip
} from "@mui/material";
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { useEffect, useMemo, useState } from "react";
import { ApiService } from "../../../Services/ApiService";
import { IUnitConfigurationFilter } from "../../../Models/IUnitConfigurationFilter";
import { getLastSplitFromKey, newUnitConfigurationFilter } from "../../../Utils/UnitConfigurationHelper";
import { IUnitConfiguration } from "../../../Models/IUnitConfiguration";
import { PropertyConstants } from "../../../Models/Constants";
import logger from '../../../Services/LoggingService';

interface DropdownOption {
  key: string;
  text: string;
  disabled?: boolean;
}

interface ComboBoxOption {
  key: string;
  text: string;
  disabled?: boolean;
  itemType?: string;
}

/**
 * User configuration management filter component using Material UI
 *
 * @param {{onChange: any, filter: IUnitConfigurationFilter, isFiltering: boolean, filteredData: IUnitConfiguration[], currentData: IUnitConfiguration[]}} props
 * @return {*} 
 */
export const UCManagementFilter: React.FC<{ 
  onChange: any, 
  filter: IUnitConfigurationFilter, 
  isFiltering: boolean, 
  filteredData: IUnitConfiguration[], 
  currentData: IUnitConfiguration[] 
}> = (props) => {
    const ctx: IMsalContext = useMsal();
    const apiService = useMemo(() => new ApiService(ctx), [ctx]);
    const theme = useTheme();
    const filteredData = props.filteredData;
    const currentData = props.currentData;

    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [filter, setFilter] = useState<IUnitConfigurationFilter>(props.filter);
    const [isFiltering, setIsFiltering] = useState<boolean>(props.isFiltering);

    const [selectedIndustry, setSelectedIndustry] = useState<string>(filter.industry);
    const [selectedSubIndustry, setSelectedSubIndustry] = useState<string>(filter.subIndustry);
    const [selectedApplication, setSelectedApplication] = useState<string>(filter.application);
    const [selectedSubApplication, setSelectedSubApplication] = useState<string>(filter.subApplication);
    const [selectedProductLine, setSelectedProductLine] = useState<string>(filter.productLine);
    const [selectedProcessStage, setSelectedProcessStage] = useState<string>((filter.processStage));
    const [selectedAlfaLavalPosition, setSelectedAlfaLavalPosition] = useState<string>((filter.alfaLavalPosition));
    const [singleSectionUnits, setSingleSectionUnits] = useState<boolean>(filter.singleSectionUnits);
    const [multiSectionUnits, setMultiSectionUnits] = useState<boolean>(filter.multiSectionUnits);
    const [disabledUnits, setDisabledUnits] = useState<boolean>(filter.disabledUnits);
    const [selectedPheTypes, setSelectedPheTypes] = useState<string[]>(filter.pheTypes);

    const [industries, setIndustries] = useState<DropdownOption[]>([]);
    const [subIndustries, setSubIndustries] = useState<DropdownOption[]>([]);
    const [applications, setApplications] = useState<DropdownOption[]>([]);
    const [subApplications, setSubApplications] = useState<DropdownOption[]>([]);
    const [productLines, setProductLines] = useState<DropdownOption[]>([]);
    const [processStages, setProcessStages] = useState<DropdownOption[]>([]);
    const [alfaLavalPositions, setAlfaLavalPositions] = useState<DropdownOption[]>([]);
    const [pheTypesOptions, setPheTypesOptions] = useState<ComboBoxOption[]>([]);

    // Styles following Material UI patterns from other components
    const styles = {
        container: {
            padding: theme.spacing(2),
        },
        outerBox: {
            display: 'flex',
            gap: theme.spacing(3),
        },
        leftSection: {
            padding: theme.spacing(2),
            backgroundColor: theme.palette.background.paper,
            borderRadius: theme.shape.borderRadius,
            boxShadow: theme.shadows[1],
            width: '300px',
            flexShrink: 0,
        },
        rightSection: {
            padding: theme.spacing(2),
            backgroundColor: theme.palette.background.paper,
            borderRadius: theme.shape.borderRadius,
            boxShadow: theme.shadows[1],
            flexGrow: 1,
        },
        filtersGrid: {
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: theme.spacing(2),
        },
        filterItem: {
            width: '100%',
        },
        checkboxSection: {
            display: 'flex',
            flexDirection: 'column',
            gap: theme.spacing(1),
            marginBottom: theme.spacing(2),
        },
        pheTypeSection: {
            marginTop: theme.spacing(3),
        },
        loadingContainer: {
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            padding: theme.spacing(4),
        }
    };

    useEffect(() => {
        getUnitConfiguration();
    }, [filteredData]);

    async function getUnitConfiguration() {
        setIsLoading(true);

        if (filteredData.length === 0) {
            setIsLoading(false);
            return;
        }
        
        let inds: DropdownOption[] = [];
        let subInds: DropdownOption[] = [];
        let apps: DropdownOption[] = [];
        let subApps: DropdownOption[] = [];
        let lines: DropdownOption[] = [];
        let stages: DropdownOption[] = [];
        let positions: DropdownOption[] = [];
        let pheTypes: ComboBoxOption[] = [];

        let relevantUnits = currentData;
        let applicableUnits = currentData;
        let notApplicableUnits: IUnitConfiguration[] = [];

        logger.debug('Processing unit configuration data for filtering', {
            dataCount: currentData.length,
            filterApplied: {
                singleSection: filter.singleSectionUnits,
                multiSection: filter.multiSectionUnits,
                disabled: filter.disabledUnits,
                pheTypesCount: filter.pheTypes.length
            }
        }, {
            component: 'UCManagementFilter',
            method: 'getUnitConfiguration'
        });
        applicableUnits = currentData;

        if (filter.singleSectionUnits == false) {
            applicableUnits = applicableUnits.filter((x) => x.isMultiSection === true);
        }
        if (filter.multiSectionUnits == false) {
            applicableUnits = applicableUnits.filter((x) => x.isMultiSection === false);
        }
        if (filter.disabledUnits == false) {
            applicableUnits = applicableUnits.filter((x) => x.isDisabled === false);
        }
        if (filter.pheTypes.length > 0) {
            applicableUnits = applicableUnits.filter(c => c.pheTypes?.some(pt => filter.pheTypes.includes(pt)));
        }

        relevantUnits = applicableUnits;

        //filter applicable units to only include ones with a matching phetype from the filter
        if (filter.pheTypes.length > 0) {
            applicableUnits = applicableUnits.filter(c => c.pheTypes?.some(pt => filter.pheTypes.includes(pt)));
            notApplicableUnits = currentData.filter(c => !c.pheTypes?.some(pt => filter.pheTypes.includes(pt)));
        }

        //Handle industry filter
        applicableUnits.forEach((unit: IUnitConfiguration) => {
            if (!inds.some((i) => i.text === unit.industry)) {
                inds.push({
                    key: unit.industry,
                    text: unit.industry
                });
            }
        });

        //Add industries that are not applicable
        notApplicableUnits.forEach((unit: IUnitConfiguration) => {
            if (!inds.some((i) => i.text === unit.industry)) {
                inds.push({
                    key: unit.industry,
                    text: unit.industry,
                    disabled: true
                });
            }
        });
        if (selectedIndustry !== '' || applicableUnits.length === 0) {
            applicableUnits = applicableUnits.filter((x) => x.industry == filter.industry);
            notApplicableUnits = currentData.filter((x) => x.industry != filter.industry);
        }

        //Handle sub industry filter
        applicableUnits.forEach((unit: IUnitConfiguration) => {
            if (!subInds.some((s) => s.text === unit.subIndustry)) {
                subInds.push({
                    key: `${unit.industry}-${unit.subIndustry}`,
                    text: unit.subIndustry
                });
            }
        });

        //Add sub industries that are not applicable
        notApplicableUnits.forEach((unit: IUnitConfiguration) => {
            if (!subInds.some((s) => s.text === unit.subIndustry)) {
                subInds.push({
                    key: `${unit.industry}-${unit.subIndustry}`,
                    text: unit.subIndustry,
                    disabled: true
                });
            }
        });

        if (selectedSubIndustry !== '') {
            applicableUnits = applicableUnits.filter((x) => x.subIndustry == filter.subIndustry);
            notApplicableUnits = currentData.filter((x) => x.subIndustry != filter.subIndustry);
        }

        //Handle application filter
        applicableUnits.forEach((unit: IUnitConfiguration) => {
            if (!apps.some((a) => a.text === unit.application)) {
                apps.push({
                    key: `${unit.industry}-${unit.subIndustry}-${unit.application}`,
                    text: unit.application
                });
            }
        });

        //Add applications that are not applicable
        notApplicableUnits.forEach((unit: IUnitConfiguration) => {
            if (!apps.some((a) => a.text === unit.application)) {
                apps.push({
                    key: `${unit.industry}-${unit.subIndustry}-${unit.application}`,
                    text: unit.application,
                    disabled: true
                });
            }
        });

        if (selectedApplication !== '') {
            notApplicableUnits = currentData.filter((x) => x.application !== filter.application);
            applicableUnits = applicableUnits.filter((x) => x.application === filter.application);
        }

        //Handle sub application filter
        applicableUnits.forEach((unit: IUnitConfiguration) => {
            if (!subApps.some((s) => s.text === unit.subApplication)) {
                subApps.push({
                    key: `${unit.industry}-${unit.subIndustry}-${unit.application}-${unit.subApplication}`,
                    text: unit.subApplication
                });
            }
        });

        //Add sub applications that are not applicable
        notApplicableUnits.forEach((unit: IUnitConfiguration) => {
            if (!subApps.some((s) => s.text === unit.subApplication)) {
                subApps.push({
                    key: `${unit.industry}-${unit.subIndustry}-${unit.application}-${unit.subApplication}`,
                    text: unit.subApplication,
                    disabled: true
                });
            }
        });

        if (selectedSubApplication !== '') {
            notApplicableUnits = currentData.filter((x) => x.subApplication !== filter.subApplication);
            applicableUnits = applicableUnits.filter((x) => x.subApplication === filter.subApplication);
        }

        //Handle product line filter
        applicableUnits.forEach((unit: IUnitConfiguration) => {
            if (!lines.some((l) => l.text === unit.productLine)) {
                lines.push({
                    key: `${unit.industry}-${unit.subIndustry}-${unit.application}-${unit.subApplication}-${unit.productLine}`,
                    text: unit.productLine
                });
            }
        });

        //Add product lines that are not applicable
        notApplicableUnits.forEach((unit: IUnitConfiguration) => {
            if (!lines.some((l) => l.text === unit.productLine)) {
                lines.push({
                    key: `${unit.industry}-${unit.subIndustry}-${unit.application}-${unit.subApplication}-${unit.productLine}`,
                    text: unit.productLine,
                    disabled: true
                });
            }
        });

        if (selectedProductLine !== '') {
            notApplicableUnits = currentData.filter((x) => x.productLine !== filter.productLine);
            applicableUnits = applicableUnits.filter((x) => x.productLine === filter.productLine);
        }

        //Handle process stage filter
        applicableUnits.forEach((unit: IUnitConfiguration) => {
            if (!stages.some((s) => s.text === unit.processStage)) {
                stages.push({
                    key: `${unit.industry}-${unit.subIndustry}-${unit.application}-${unit.subApplication}-${unit.productLine}-${unit.processStage}`,
                    text: unit.processStage
                });
            }
        });

        // Add process stages that are not applicable
        notApplicableUnits.forEach((unit: IUnitConfiguration) => {
            if (!stages.some((s) => s.text === unit.processStage)) {
                stages.push({
                    key: `${unit.industry}-${unit.subIndustry}-${unit.application}-${unit.subApplication}-${unit.productLine}-${unit.processStage}`,
                    text: unit.processStage,
                    disabled: true
                });
            }
        });

        if (selectedProcessStage !== '') {
            notApplicableUnits = currentData.filter((x) => x.processStage !== filter.processStage);
            applicableUnits = applicableUnits.filter((x) => x.processStage === filter.processStage);
        }

        //Handle alfa laval position filter
        applicableUnits.forEach((unit: IUnitConfiguration) => {
            if (!positions.some((p) => p.text === unit.alfaLavalPosition)) {
                positions.push({
                    key: `${unit.industry}-${unit.subIndustry}-${unit.application}-${unit.subApplication}-${unit.productLine}-${unit.processStage}-${unit.alfaLavalPosition}`,
                    text: unit.alfaLavalPosition
                });
            }
        });

        // Add alfa laval positions that are not applicable
        notApplicableUnits.forEach((unit: IUnitConfiguration) => {
            if (!positions.some((p) => p.text === unit.alfaLavalPosition)) {
                positions.push({
                    key: `${unit.industry}-${unit.subIndustry}-${unit.application}-${unit.subApplication}-${unit.productLine}-${unit.processStage}-${unit.alfaLavalPosition}`,
                    text: unit.alfaLavalPosition,
                    disabled: true
                });
            }
        });

        if (selectedAlfaLavalPosition !== '') {
            notApplicableUnits = currentData.filter((x) => x.alfaLavalPosition !== filter.alfaLavalPosition);
            applicableUnits = applicableUnits.filter((x) => x.alfaLavalPosition === filter.alfaLavalPosition);
        }

        //Get PHE types from all units
        applicableUnits.forEach((unit: IUnitConfiguration) => {
            unit.pheTypes?.forEach((pheType) => {
                //check phetype is not already added
                if (!pheTypes.some((p) => p.text === pheType)) {
                    pheTypes.push({
                        key: pheType,
                        text: pheType
                    });
                }
            });
        });

        notApplicableUnits.forEach((unit: IUnitConfiguration) => {
            unit.pheTypes?.forEach((pheType) => {
                //check phetype is not already added
                if (!pheTypes.some((p) => p.text === pheType)) {
                    pheTypes.push({
                        key: pheType,
                        text: pheType,
                        disabled: true
                    });
                }
            });
        });

        setIndustries(inds);
        setSubIndustries(subInds);
        setApplications(apps);
        setSubApplications(subApps);
        setProductLines(lines);
        setProcessStages(stages);
        setAlfaLavalPositions(positions);
        setPheTypesOptions(pheTypes);

        setIsLoading(false);
    }

    useEffect(() => {
        const newFilter = filter;
        newFilter.industry = getLastSplitFromKey(selectedIndustry);
        newFilter.subIndustry = getLastSplitFromKey(selectedSubIndustry);
        newFilter.application = getLastSplitFromKey(selectedApplication);
        newFilter.subApplication = getLastSplitFromKey(selectedSubApplication);
        newFilter.productLine = getLastSplitFromKey(selectedProductLine);
        newFilter.processStage = getLastSplitFromKey(selectedProcessStage);
        newFilter.alfaLavalPosition = getLastSplitFromKey(selectedAlfaLavalPosition);
        newFilter.singleSectionUnits = singleSectionUnits;
        newFilter.multiSectionUnits = multiSectionUnits;
        newFilter.disabledUnits = disabledUnits;
        newFilter.pheTypes = selectedPheTypes;
        setFilter(newFilter);
        props.onChange(newFilter, isFiltering);

    }, [selectedIndustry, selectedSubIndustry, selectedApplication, selectedSubApplication, selectedProcessStage, selectedProductLine, selectedAlfaLavalPosition, singleSectionUnits, multiSectionUnits, disabledUnits, isFiltering, selectedPheTypes]);

    /**
     * Handle change event for dropdowns in the filter component 
     *
     * @param {string} value
     * @param {string} name
     */
    const handleChange = (value: string, name: string) => {
        switch (name) {
            case 'industry':
                logger.debug('Industry filter selected', {
                    selectedValue: value,
                    availableIndustries: industries.length
                }, {
                    component: 'UCManagementFilter',
                    method: 'handleChange'
                });
                setSelectedIndustry(value);
                setSelectedSubIndustry('');
                setSelectedApplication('');
                setSelectedSubApplication('');
                setSelectedProductLine('');
                setSelectedProcessStage('');
                setSelectedAlfaLavalPosition('');
                break;
            case 'subIndustry':
                setSelectedSubIndustry(value);
                setSelectedApplication('');
                setSelectedSubApplication('');
                setSelectedProductLine('');
                setSelectedProcessStage('');
                setSelectedAlfaLavalPosition('');
                break;
            case 'application':
                setSelectedApplication(value);
                setSelectedSubApplication('');
                setSelectedProductLine('');
                setSelectedProcessStage('');
                setSelectedAlfaLavalPosition('');
                break;
            case 'subApplication':
                setSelectedSubApplication(value);
                setSelectedProductLine('');
                setSelectedProcessStage('');
                setSelectedAlfaLavalPosition('');
                break;
            case 'productLine':
                setSelectedProductLine(value);
                setSelectedProcessStage('');
                setSelectedAlfaLavalPosition('');
                break;
            case 'processStage':
                setSelectedProcessStage(value);
                setSelectedAlfaLavalPosition('');
                break;
            case 'alfaLavalPosition':
                setSelectedAlfaLavalPosition(value);
                break;
            default:
                break;
        }

        setIsFiltering(true);
    }

    /**
     * Handle change event for checkboxes in the filter component 
     *
     * @param {string} name
     */
    const handleCheckboxChange = (name: string) => {
        switch (name) {
            case 'singleSectionUnits':
                setSingleSectionUnits(!singleSectionUnits);
                break;
            case 'multiSectionUnits':
                setMultiSectionUnits(!multiSectionUnits);
                break;
            case 'disabledUnits':
                setDisabledUnits(!disabledUnits);
                break;
            default:
                break;
        }

        setIsFiltering(true);
    }

    /**
     * Handle change event for PHE types autocomplete
     *
     * @param {string[]} newValue
     */
    const handlePHETypesChange = (newValue: string[]) => {
        logger.debug('PHE types filter changed', {
            selectedCount: newValue.length,
            selectedTypes: newValue
        }, {
            component: 'UCManagementFilter',
            method: 'handlePHETypesChange'
        });
        setSelectedPheTypes(newValue);
        setIsFiltering(true);
    };

    /**
     * Reset filter to default values
     */
    const resetFilter = () => {
        const blankFilter = newUnitConfigurationFilter();
        setSelectedIndustry('');
        setSelectedSubIndustry('');
        setSelectedApplication('');
        setSelectedSubApplication('');
        setSelectedProductLine('');
        setSelectedProcessStage('');
        setSelectedAlfaLavalPosition('');
        setSingleSectionUnits(true);
        setMultiSectionUnits(true);
        setDisabledUnits(false);
        setSelectedPheTypes([]);
        setFilter(blankFilter);
        setIsFiltering(false);
        props.onChange(blankFilter, false);
    }

    if (isLoading) {
        return (
            <Box sx={styles.loadingContainer}>
                <CircularProgress size={40} />
                <Typography variant="body1" ml={2}>
                    Loading filters...
                </Typography>
            </Box>
        );
    }

    return (
        <Box sx={styles.container}>
            <Box sx={styles.outerBox}>
                {/* Left section: Checkboxes and PHE Types */}
                <Box sx={styles.leftSection}>
                    <Box sx={styles.checkboxSection}>
                        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                            Show:
                        </Typography>
                        <FormControlLabel
                            control={
                                <Checkbox 
                                    checked={singleSectionUnits} 
                                    onChange={() => handleCheckboxChange('singleSectionUnits')} 
                                />
                            }
                            label="Single section units"
                        />
                        <FormControlLabel
                            control={
                                <Checkbox 
                                    checked={multiSectionUnits} 
                                    onChange={() => handleCheckboxChange('multiSectionUnits')} 
                                />
                            }
                            label="Multisection units"
                        />
                        <FormControlLabel
                            control={
                                <Checkbox 
                                    checked={disabledUnits} 
                                    onChange={() => handleCheckboxChange('disabledUnits')} 
                                />
                            }
                            label="Disabled configurations"
                        />
                    </Box>
                    
                    <Box sx={styles.pheTypeSection}>
                        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                            Only PHE Types
                        </Typography>
                        <Autocomplete
                            multiple
                            options={pheTypesOptions.filter(option => !option.disabled).map(option => option.text)}
                            value={selectedPheTypes}
                            onChange={(event, newValue) => handlePHETypesChange(newValue)}
                            renderTags={(value, getTagProps) =>
                                value.map((option, index) => (
                                    <Chip
                                        variant="outlined"
                                        label={option}
                                        {...getTagProps({ index })}
                                        key={option}
                                    />
                                ))
                            }
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    variant="outlined"
                                    size="small"
                                    placeholder="Select PHE types"
                                />
                            )}
                            size="small"
                        />
                    </Box>
                </Box>

                {/* Right section: Filter dropdowns */}
                <Box sx={styles.rightSection}>
                    <Box sx={styles.filtersGrid}>
                        <FormControl size="small" sx={styles.filterItem}>
                            <InputLabel id="industry-label">Industry</InputLabel>
                            <Select
                                labelId="industry-label"
                                label="Industry"
                                value={selectedIndustry}
                                onChange={(e) => handleChange(e.target.value, 'industry')}
                                disabled={isLoading}
                            >
                                <MenuItem value="">
                                    <em>All</em>
                                </MenuItem>
                                {industries.map((option) => (
                                    <MenuItem 
                                        key={option.key} 
                                        value={option.key} 
                                        disabled={option.disabled}
                                    >
                                        {option.text}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <FormControl size="small" sx={styles.filterItem}>
                            <InputLabel id="subindustry-label">Sub Industry</InputLabel>
                            <Select
                                labelId="subindustry-label"
                                label="Sub Industry"
                                value={selectedSubIndustry}
                                onChange={(e) => handleChange(e.target.value, 'subIndustry')}
                                disabled={isLoading}
                            >
                                <MenuItem value="">
                                    <em>All</em>
                                </MenuItem>
                                {subIndustries.map((option) => (
                                    <MenuItem 
                                        key={option.key} 
                                        value={option.key} 
                                        disabled={option.disabled}
                                    >
                                        {option.text}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <FormControl size="small" sx={styles.filterItem}>
                            <InputLabel id="application-label">Application</InputLabel>
                            <Select
                                labelId="application-label"
                                label="Application"
                                value={selectedApplication}
                                onChange={(e) => handleChange(e.target.value, 'application')}
                                disabled={isLoading}
                            >
                                <MenuItem value="">
                                    <em>All</em>
                                </MenuItem>
                                {applications.map((option) => (
                                    <MenuItem 
                                        key={option.key} 
                                        value={option.key} 
                                        disabled={option.disabled}
                                    >
                                        {option.text}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <FormControl size="small" sx={styles.filterItem}>
                            <InputLabel id="subapplication-label">Sub application</InputLabel>
                            <Select
                                labelId="subapplication-label"
                                label="Sub application"
                                value={selectedSubApplication}
                                onChange={(e) => handleChange(e.target.value, 'subApplication')}
                                disabled={isLoading}
                            >
                                <MenuItem value="">
                                    <em>All</em>
                                </MenuItem>
                                {subApplications.map((option) => (
                                    <MenuItem 
                                        key={option.key} 
                                        value={option.key} 
                                        disabled={option.disabled}
                                    >
                                        {option.text}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <FormControl size="small" sx={styles.filterItem}>
                            <InputLabel id="productline-label">Product line</InputLabel>
                            <Select
                                labelId="productline-label"
                                label="Product line"
                                value={selectedProductLine}
                                onChange={(e) => handleChange(e.target.value, 'productLine')}
                                disabled={isLoading}
                            >
                                <MenuItem value="">
                                    <em>All</em>
                                </MenuItem>
                                {productLines.map((option) => (
                                    <MenuItem 
                                        key={option.key} 
                                        value={option.key} 
                                        disabled={option.disabled}
                                    >
                                        {option.text}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <FormControl size="small" sx={styles.filterItem}>
                            <InputLabel id="processstage-label">Process stage</InputLabel>
                            <Select
                                labelId="processstage-label"
                                label="Process stage"
                                value={selectedProcessStage}
                                onChange={(e) => handleChange(e.target.value, 'processStage')}
                                disabled={isLoading}
                            >
                                <MenuItem value="">
                                    <em>All</em>
                                </MenuItem>
                                {processStages.map((option) => (
                                    <MenuItem 
                                        key={option.key} 
                                        value={option.key} 
                                        disabled={option.disabled}
                                    >
                                        {option.text}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <FormControl size="small" sx={styles.filterItem}>
                            <InputLabel id="position-label">Position</InputLabel>
                            <Select
                                labelId="position-label"
                                label="Position"
                                value={selectedAlfaLavalPosition}
                                onChange={(e) => handleChange(e.target.value, 'alfaLavalPosition')}
                                disabled={isLoading}
                            >
                                <MenuItem value="">
                                    <em>All</em>
                                </MenuItem>
                                {alfaLavalPositions.map((option) => (
                                    <MenuItem 
                                        key={option.key} 
                                        value={option.key} 
                                        disabled={option.disabled}
                                    >
                                        {option.text}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>

                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                        <Button
                            variant="outlined"
                            startIcon={<RestartAltIcon />}
                            onClick={resetFilter}
                            sx={{ minWidth: '120px' }}
                        >
                            Reset
                        </Button>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
}; 